// client/src/pages/application/DashboardPage.js
import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { businessAPI } from '../../services/businessAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const API_URL_DASH = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const VIDEO_FORMATS = new Set([
  'video', 'reel', 'reels', 'short', 'shorts',
  'tiktok', 'youtube', 'youtube_short', 'youtube_shorts', 'live',
]);

const PLATFORMS = {
  instagram:      { icon: 'fab fa-instagram',   bg: 'bg-pink-600',    label: 'Instagram' },
  facebook:       { icon: 'fab fa-facebook-f',  bg: 'bg-blue-600',    label: 'Facebook'  },
  linkedin:       { icon: 'fab fa-linkedin-in', bg: 'bg-blue-700',    label: 'LinkedIn'  },
  twitter:        { icon: 'fab fa-x-twitter',   bg: 'bg-slate-800',   label: 'Twitter/X' },
  tiktok:         { icon: 'fab fa-tiktok',      bg: 'bg-slate-900',   label: 'TikTok'    },
  youtube:        { icon: 'fab fa-youtube',     bg: 'bg-red-600',     label: 'YouTube'   },
  email:          { icon: 'fas fa-envelope',    bg: 'bg-emerald-600', label: 'Email'     },
  googleBusiness: { icon: 'fab fa-google',      bg: 'bg-amber-500',   label: 'Google'    },
};

const FORMAT_LABELS = {
  reel: 'Reel', reels: 'Reel', carousel: 'Carousel', story: 'Story', stories: 'Story',
  post: 'Post', video: 'Video', short: 'Short', shorts: 'Short', live: 'Live',
  newsletter: 'Newsletter', standard: 'Post', blog: 'Blog', article: 'Article',
  tiktok: 'TikTok', youtube: 'YT Short', youtube_short: 'YT Short', youtube_shorts: 'YT Short',
};

const STATUS_CONFIG = {
  completed: { dot: 'bg-green-500',  label: 'Completed', text: 'text-green-600',  badge: 'bg-green-50 text-green-700'  },
  approved:  { dot: 'bg-blue-500',   label: 'Approved',  text: 'text-blue-600',   badge: 'bg-blue-50 text-blue-700'    },
  edited:    { dot: 'bg-indigo-400', label: 'Edited',    text: 'text-indigo-600', badge: 'bg-indigo-50 text-indigo-700'},
  draft:     { dot: 'bg-slate-300',  label: 'Draft',     text: 'text-slate-400',  badge: 'bg-slate-100 text-slate-500' },
};

const isVideo = fmt => VIDEO_FORMATS.has((fmt || '').toLowerCase());

const daysUntil = dateStr => {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const d   = new Date(dateStr); d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86_400_000);
};


// ── Stat card — Stripe-style compact ─────────────────────────────────────────
function StatCard({ value, label, sub, subColor = 'text-slate-400', iconBg, iconClass, trend }) {
  return (
    <div className="bg-white rounded-xl p-4 border-2 border-gray-200 flex flex-col gap-3 relative overflow-hidden">
      {/* Micro grid — bottom-right fade */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(rgba(100,116,139,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(100,116,139,0.22) 1px, transparent 1px)',
        backgroundSize: '8px 8px',
        maskImage: 'radial-gradient(ellipse 80% 80% at 100% 100%, black 0%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 100% 100%, black 0%, transparent 70%)',
      }} />
      <div className="flex items-start justify-between">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <i className={`${iconClass} text-white text-xs`}></i>
        </div>
        <div className="flex items-center gap-1">
          {trend === 'up'   && <i className="fas fa-arrow-up   text-green-500 text-[9px]"></i>}
          {trend === 'down' && <i className="fas fa-arrow-down text-red-500   text-[9px]"></i>}
          {trend === 'warn' && <i className="fas fa-exclamation-circle text-amber-500 text-[9px]"></i>}
          <span className={`text-[9px] font-semibold ${subColor}`}>{sub}</span>
        </div>
      </div>
      <div>
        <p className="text-[26px] font-extrabold text-slate-800 leading-none tracking-tight">{value}</p>
        <p className="text-[11px] text-slate-400 mt-1 font-medium">{label}</p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const [searchParams] = useSearchParams();
  const navigate        = useNavigate();

  const [loading, setLoading]           = useState(true);
  const [calendarDays, setCalendarDays] = useState([]);
  const [strategyData, setStrategyData] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [showSuccess, setShowSuccess]   = useState(false);
  const [subData, setSubData]           = useState(null);

  useEffect(() => {
    if (searchParams.get('subscription') === 'success') {
      setShowSuccess(true);
      navigate('/dashboard', { replace: true });
      setTimeout(() => setShowSuccess(false), 8000);
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileRes, strategyRes, roadmapRes] = await Promise.all([
        businessAPI.getProfile().catch(() => null),
        businessAPI.getStrategy().catch(() => null),
        businessAPI.getRoadmap().catch(() => null),
      ]);
      if (profileRes?.success)  setBusinessName(profileRes.profile?.businessName || '');
      if (strategyRes?.success) setStrategyData(strategyRes.strategy);
      if (roadmapRes?.success && roadmapRes.exists)
        setCalendarDays(roadmapRes.roadmap?.calendar?.days || []);

      // Load subscription (non-blocking)
      fetch(`${API_URL_DASH}/api/subscription`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json()).then(d => { if (d.success) setSubData(d.data); }).catch(() => {});
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const todayISO = new Date().toISOString().split('T')[0];

  const todayDay = useMemo(
    () => calendarDays.find(d => d.date === todayISO) || null,
    [calendarDays, todayISO]
  );

  const next7 = useMemo(() => {
    const nowMs = new Date(todayISO).getTime();
    return calendarDays
      .filter(d => { const ms = new Date(d.date).getTime(); return ms > nowMs && ms <= nowMs + 7 * 86_400_000; })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 7);
  }, [calendarDays, todayISO]);

  const stats = useMemo(() => {
    const total = calendarDays.length;
    if (!total) return { total: 0, completed: 0, approved: 0, edited: 0, draft: 0, pct: 0 };
    const completed = calendarDays.filter(d => d.status === 'completed').length;
    const approved  = calendarDays.filter(d => d.status === 'approved').length;
    const edited    = calendarDays.filter(d => d.status === 'edited').length;
    const draft     = total - completed - approved - edited;
    const pct       = Math.round(((completed + approved) / total) * 100);
    return { total, completed, approved, edited, draft: Math.max(0, draft), pct };
  }, [calendarDays]);

  const videoAlerts = next7.filter(d => isVideo(d.format) && d.status !== 'completed').length;
  const readyCount  = next7.filter(d => d.status === 'completed' || d.status === 'approved').length;

  // Progress ring (r=40, viewBox 110×110, centre 55,55)
  const RADIUS = 40;
  const CIRC   = 2 * Math.PI * RADIUS;
  const dashOffset = CIRC * (1 - stats.pct / 100);

  // ── PDF ───────────────────────────────────────────────────────────────────
  const downloadReport = () => {
    const total      = calendarDays.length;
    const completed  = calendarDays.filter(d => d.status === 'completed').length;
    const approved   = calendarDays.filter(d => d.status === 'approved').length;
    const rate       = total ? Math.round(((completed + approved) / total) * 100) : 0;
    const reportDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const smartGoal  = strategyData?.smartGoal?.fullStatement || strategyData?.megaStrategy?.strategySummary?.smartGoal || '';

    const platCounts = {};
    calendarDays.forEach(d => { const p = d.platform || 'instagram'; platCounts[p] = (platCounts[p] || 0) + 1; });

    const sc = { completed: '#16a34a', approved: '#d97706', edited: '#2563eb', draft: '#94a3b8' };
    const sl = { completed: '✓ Done', approved: 'Approved', edited: 'Edited', draft: 'Draft' };

    const platRows = Object.entries(platCounts).map(([p, n]) =>
      `<tr><td>${PLATFORMS[p]?.label || p}</td><td><b>${n}</b></td><td>${Math.round((n / total) * 100)}%</td></tr>`).join('');

    const calRows = calendarDays.map(d => {
      const date = new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      return `<tr><td style="color:#64748b;font-weight:700">Day ${d.day}</td><td>${date}</td>
        <td>${PLATFORMS[d.platform]?.label || d.platform || '-'}</td>
        <td>${d.postTitle || '-'}</td><td>${FORMAT_LABELS[d.format] || d.format || '-'}</td>
        <td style="color:${sc[d.status] || '#64748b'};font-weight:700">${sl[d.status] || d.status || '-'}</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">
<title>${businessName || 'Business'} — 30-Day Marketing Report</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;color:#1e293b;background:#fff}
.wrap{max-width:900px;margin:0 auto;padding:48px}
.hdr{background:#0f172a;color:#fff;padding:36px 48px;margin:-48px -48px 36px}
.hdr h1{font-size:24px;font-weight:800;margin-bottom:6px}.hdr p{font-size:13px;color:rgba(255,255,255,.5)}
.badge{display:inline-block;background:rgba(34,197,94,.2);color:#22c55e;padding:3px 10px;border-radius:4px;font-size:10px;font-weight:700;margin-top:10px}
.sec{margin-bottom:28px}.st{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:#94a3b8;margin-bottom:12px;padding-bottom:8px;border-bottom:2px solid #f1f5f9}
.g4{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}.stat{background:#f8fafc;border-radius:10px;padding:16px;text-align:center}
.stat-n{font-size:26px;font-weight:800}.stat-l{font-size:10px;color:#94a3b8;margin-top:4px;text-transform:uppercase;letter-spacing:.06em}
.smart{background:#f0fdf4;border-left:4px solid #22c55e;padding:14px 18px;border-radius:0 8px 8px 0;font-size:13px;line-height:1.65;color:#166534}
table{width:100%;border-collapse:collapse}thead th{background:#f8fafc;padding:8px 12px;text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;color:#94a3b8}
td{padding:7px 12px;border-bottom:1px solid #f1f5f9;font-size:11px}
.footer{margin-top:40px;padding-top:16px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:11px;color:#94a3b8}
@media print{.wrap{padding:0}.hdr{margin:0 0 28px}}</style></head>
<body><div class="wrap">
<div class="hdr"><h1>${businessName || 'Business'}</h1><p>30-Day Content Marketing Campaign Report</p><div class="badge">Generated ${reportDate}</div></div>
<div class="sec"><div class="st">Campaign Overview</div><div class="g4">
<div class="stat"><div class="stat-n">${total}</div><div class="stat-l">Total Posts</div></div>
<div class="stat"><div class="stat-n" style="color:#22c55e">${completed}</div><div class="stat-l">Completed</div></div>
<div class="stat"><div class="stat-n" style="color:#f59e0b">${approved}</div><div class="stat-l">Approved</div></div>
<div class="stat"><div class="stat-n" style="color:#3b82f6">${rate}%</div><div class="stat-l">Completion</div></div></div></div>
${smartGoal ? `<div class="sec"><div class="st">SMART Goal</div><div class="smart">${smartGoal}</div></div>` : ''}
<div class="sec"><div class="st">Platform Distribution</div><table><thead><tr><th>Platform</th><th>Posts</th><th>Share</th></tr></thead><tbody>${platRows}</tbody></table></div>
<div class="sec"><div class="st">30-Day Content Calendar</div><table><thead><tr><th>Day</th><th>Date</th><th>Platform</th><th>Post Title</th><th>Format</th><th>Status</th></tr></thead><tbody>${calRows}</tbody></table></div>
<div class="footer"><span>Digital Trent Marketing Studio</span><span>${reportDate}</span></div>
</div></body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const w    = window.open(url, '_blank');
    if (w) w.onload = () => { w.print(); URL.revokeObjectURL(url); };
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    </DashboardLayout>
  );

  const todayFormatted = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const pl = todayDay ? (PLATFORMS[todayDay.platform] || PLATFORMS.instagram) : null;
  const todaySt = todayDay ? (STATUS_CONFIG[todayDay.status] || STATUS_CONFIG.draft) : null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="min-h-screen bg-slate-50 flex flex-col">

        {/* ── Sticky header ──────────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-10 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                {businessName || 'Dashboard'}
              </h1>
              <p className="text-sm text-gray-500 mt-1 font-medium">{todayFormatted}</p>
            </div>
            <Link to="/marketing"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors shadow-sm shadow-blue-600/20">
              <i className="fas fa-rocket text-[10px]"></i>
              Marketing Studio
            </Link>
          </div>
        </div>

        {/* Success banner */}
        {showSuccess && (
          <div className="mx-5 mt-4 bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
            <i className="fas fa-check-circle text-green-500 text-sm flex-shrink-0"></i>
            <p className="text-xs font-semibold text-green-800 flex-1">Subscription activated — all features are now unlocked.</p>
            <button onClick={() => setShowSuccess(false)} className="text-green-400 hover:text-green-600">
              <i className="fas fa-times text-xs"></i>
            </button>
          </div>
        )}

        <div className="p-5 space-y-4 flex-1">

          {/* ══════════════════════════════════════════════════════════
              ROW 1 — 4 compact stat cards
          ══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

            <StatCard
              value={stats.total || '—'}
              label="Total Posts"
              sub={stats.total ? `${stats.total}-day plan` : 'No campaign yet'}
              subColor="text-slate-400"
              iconBg="bg-blue-600"
              iconClass="fas fa-calendar-alt"
            />

            <StatCard
              value={stats.completed}
              label="Posts Completed"
              sub={stats.total ? `${Math.round((stats.completed / stats.total) * 100)}% of plan` : 'No data'}
              subColor="text-green-500"
              trend={stats.completed > 0 ? 'up' : undefined}
              iconBg="bg-green-500"
              iconClass="fas fa-check-double"
            />

            <StatCard
              value={`${stats.pct}%`}
              label="Campaign Progress"
              sub={stats.approved ? `${stats.approved} approved` : 'In progress'}
              subColor="text-blue-500"
              trend={stats.pct > 0 ? 'up' : undefined}
              iconBg="bg-blue-700"
              iconClass="fas fa-chart-line"
            />

            {/* Subscription / plan tier card */}
            {(() => {
              const isPro         = subData?.currentTier === 'pro' && subData?.status === 'active';
              const isTrialActive = subData?.status === 'trial';
              const trialDays     = subData?.trialDaysRemaining ?? 0;
              const warn          = !isPro && isTrialActive && trialDays <= 3;
              return (
                <Link to="/settings?tab=subscription" className="block">
                  <StatCard
                    value={isPro ? 'Pro' : isTrialActive ? `${trialDays}d` : 'Expired'}
                    label={isPro ? 'Plan: Pro' : 'Free Trial'}
                    sub={isPro ? '£39 / month' : isTrialActive ? 'trial remaining' : 'Upgrade now'}
                    subColor={isPro ? 'text-blue-500' : warn ? 'text-red-500' : 'text-amber-500'}
                    trend={warn || (!isPro && !isTrialActive) ? 'warn' : undefined}
                    iconBg={isPro ? 'bg-blue-600' : warn ? 'bg-red-500' : 'bg-amber-500'}
                    iconClass={isPro ? 'fas fa-crown' : 'fas fa-clock'}
                  />
                </Link>
              );
            })()}

          </div>

          {/* ══════════════════════════════════════════════════════════
              ROW 2 — Asymmetric: Left 2-col stack + Right sidebar
          ══════════════════════════════════════════════════════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* ── Left column — Content Schedule (merged, vertical list) ── */}
            <div className="lg:col-span-2 flex flex-col">
              <div className="bg-white rounded-xl border-2 border-gray-200 flex-1 flex flex-col overflow-hidden">

                {/* Card header */}
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-2.5">
                    <h2 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Content Schedule</h2>
                    {videoAlerts > 0 && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-white bg-amber-500 px-3 py-1.5 rounded-full shadow shadow-amber-400/40">
                        <i className="fas fa-video text-[10px]"></i>
                        {videoAlerts} video{videoAlerts > 1 ? 's' : ''} to film
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {next7.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-slate-400">{readyCount}/{next7.length} ready</span>
                        <div className="w-14 h-1 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all duration-700"
                            style={{ width: `${next7.length ? (readyCount / next7.length) * 100 : 0}%` }} />
                        </div>
                      </div>
                    )}
                    <Link to="/marketing" className="text-[10px] font-bold text-blue-600 hover:text-blue-800">
                      Full Calendar →
                    </Link>
                  </div>
                </div>

                {/* ── TODAY ─────────────────────────────────────────────── */}
                <div className="px-5 pt-4 pb-1 flex-shrink-0">
                  <p className="text-[9px] font-extrabold text-slate-300 uppercase tracking-[0.15em] mb-2">Today</p>
                </div>

                {todayDay ? (
                  <Link to="/marketing"
                    className="flex items-center gap-3 px-5 py-3 mx-2 rounded-xl hover:bg-slate-50 transition-colors group flex-shrink-0">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${pl?.bg || 'bg-slate-400'}`}>
                      <i className={`${pl?.icon || 'fas fa-share-alt'} text-white text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-800 group-hover:text-blue-700 transition-colors truncate leading-snug">
                        {todayDay.postTitle}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-slate-400">{pl?.label}</span>
                        <span className="w-px h-2.5 bg-slate-200 flex-shrink-0"></span>
                        <span className="text-[10px] text-slate-400">{FORMAT_LABELS[todayDay.format] || todayDay.format}</span>
                        {isVideo(todayDay.format) && todayDay.status !== 'completed' && (
                          <span className="text-[9px] font-extrabold text-red-600 bg-red-50 px-1.5 py-px rounded border border-red-100">
                            🎬 Video — open brief first
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${todaySt?.badge || 'bg-slate-100 text-slate-500'}`}>
                      {todaySt?.label || 'Draft'}
                    </span>
                    <i className="fas fa-chevron-right text-slate-200 text-[9px] group-hover:text-blue-400 transition-colors flex-shrink-0"></i>
                  </Link>
                ) : (
                  <div className="flex items-center gap-3 px-5 py-3 flex-shrink-0">
                    <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-moon text-slate-300 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-500">Rest Day</p>
                      <p className="text-[10px] text-slate-400">
                        {next7.length > 0
                          ? `Next: ${new Date(next7[0].date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}`
                          : 'No upcoming posts scheduled'}
                      </p>
                    </div>
                  </div>
                )}

                {/* ── THIS WEEK ─────────────────────────────────────────── */}
                <div className="flex items-center gap-3 px-5 pt-3 pb-2 flex-shrink-0">
                  <p className="text-[9px] font-extrabold text-slate-300 uppercase tracking-[0.15em] flex-shrink-0">This Week</p>
                  <div className="flex-1 h-px bg-gray-100"></div>
                </div>

                {/* Vertical list */}
                {next7.length > 0 ? (
                  <div className="flex-1 overflow-y-auto">
                    {next7.map((day, idx) => {
                      const n         = daysUntil(day.date);
                      const p         = PLATFORMS[day.platform] || PLATFORMS.instagram;
                      const vid       = isVideo(day.format);
                      const done      = day.status === 'completed';
                      const prepAlert = vid && !done && n >= 1 && n <= 3;
                      const fmt       = FORMAT_LABELS[day.format] || day.format || 'Post';
                      const dtStr     = new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                      const dayLbl    = n === 1 ? 'TMRW' : `+${n}d`;
                      const lblColor  = done ? 'text-green-500' : prepAlert ? 'text-amber-600' : n === 1 ? 'text-orange-500' : 'text-slate-400';
                      const isLast    = idx === next7.length - 1;

                      return (
                        <Link to="/marketing" key={day.day}
                          className={`flex items-center gap-3 px-5 py-3 transition-colors group ${
                            !isLast ? 'border-b border-gray-50' : ''
                          } ${prepAlert ? 'bg-amber-100 hover:bg-amber-100' : 'hover:bg-slate-50'}`}>

                          {/* Day label — fixed width */}
                          <span className={`text-[9px] font-extrabold uppercase tracking-wide w-9 flex-shrink-0 ${lblColor}`}>
                            {dayLbl}
                          </span>

                          {/* Platform icon */}
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-500' : p.bg}`}>
                            <i className={`${done ? 'fas fa-check' : p.icon} text-white text-[8px]`}></i>
                          </div>

                          {/* Post info */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-semibold truncate group-hover:text-blue-700 transition-colors leading-snug ${
                              done ? 'line-through text-slate-300' : 'text-slate-700'
                            }`}>
                              {day.postTitle}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[9px] text-slate-400">{dtStr}</span>
                              {prepAlert && (
                                <span className="text-[9px] font-extrabold text-amber-600">
                                  ⚡ Prepare now — {n}d to film
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Right badges */}
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-[8px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-px rounded">{fmt}</span>
                            {vid && !done && (
                              <span className="text-[8px] font-extrabold text-orange-600 bg-orange-50 border border-orange-100 px-1 py-px rounded">🎬</span>
                            )}
                            {done && <i className="fas fa-check-circle text-green-400 text-xs"></i>}
                          </div>

                          <i className="fas fa-chevron-right text-slate-100 text-[9px] group-hover:text-slate-300 transition-colors flex-shrink-0"></i>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                    <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <i className="fas fa-calendar-alt text-slate-300 text-sm"></i>
                    </div>
                    <p className="text-xs font-bold text-slate-500 mb-1">No content this week</p>
                    <p className="text-[11px] text-slate-400 mb-4">Generate your strategy to fill your schedule.</p>
                    <Link to="/marketing"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors">
                      <i className="fas fa-rocket text-[10px]"></i> Build Your Plan
                    </Link>
                  </div>
                )}

              </div>
            </div>

            {/* ── Right column — Campaign Overview (full height) ─────── */}
            <div className="bg-white rounded-xl border-2 border-gray-200 flex flex-col overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-50">
                <h2 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Campaign Overview</h2>
                <p className="text-[10px] text-slate-400 mt-0.5">30-day performance</p>
              </div>

              {calendarDays.length > 0 ? (
                <div className="p-5 flex-1 flex flex-col">
                  {/* Progress ring — r=40 */}
                  <div className="flex items-center justify-center py-2 mb-4">
                    <div className="relative">
                      <svg width="100" height="100" viewBox="0 0 110 110">
                        {/* Track */}
                        <circle cx="55" cy="55" r={RADIUS} fill="none" stroke="#f1f5f9" strokeWidth="8" />
                        {/* Overall progress arc (blue) */}
                        <circle cx="55" cy="55" r={RADIUS}
                          fill="none" stroke="#2563eb" strokeWidth="8"
                          strokeDasharray={CIRC}
                          strokeDashoffset={dashOffset}
                          strokeLinecap="round"
                          transform="rotate(-90 55 55)"
                          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                        />
                        {/* Completed arc (green) */}
                        <circle cx="55" cy="55" r={RADIUS}
                          fill="none" stroke="#22c55e" strokeWidth="8"
                          strokeDasharray={`${(stats.completed / (stats.total || 1)) * CIRC} ${CIRC}`}
                          strokeLinecap="round"
                          transform="rotate(-90 55 55)"
                          style={{ transition: 'stroke-dasharray 0.8s ease' }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-xl font-extrabold text-slate-800 leading-none">{stats.pct}%</span>
                        <span className="text-[9px] text-slate-400 font-medium mt-0.5">Complete</span>
                      </div>
                    </div>
                  </div>

                  {/* 2×2 mini stats */}
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {[
                      { label: 'Total',     value: stats.total,     color: 'text-slate-800' },
                      { label: 'Done',      value: stats.completed, color: 'text-green-600' },
                      { label: 'Approved',  value: stats.approved,  color: 'text-blue-600'  },
                      { label: 'Draft',     value: stats.draft,     color: 'text-slate-400' },
                    ].map(s => (
                      <div key={s.label} className="text-center py-2.5 rounded-lg bg-slate-100">
                        <p className={`text-lg font-extrabold leading-none ${s.color}`}>{s.value}</p>
                        <p className="text-[9px] text-slate-400 font-medium mt-1">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Progress bars */}
                  <div className="space-y-2.5 mb-5">
                    {[
                      { label: 'Completed', value: stats.completed, color: 'bg-green-500' },
                      { label: 'Approved',  value: stats.approved,  color: 'bg-blue-500'  },
                      { label: 'Draft',     value: stats.draft,     color: 'bg-slate-300' },
                    ].map(b => (
                      <div key={b.label} className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 w-16 flex-shrink-0">{b.label}</span>
                        <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${b.color} rounded-full`}
                            style={{ width: `${stats.total ? (b.value / stats.total) * 100 : 0}%`, transition: 'width 0.6s ease' }} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 w-5 text-right">{b.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Download */}
                  <button onClick={downloadReport}
                    className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-2 mt-auto">
                    <i className="fas fa-download text-[10px]"></i>
                    Download PDF Report
                  </button>
                  <p className="text-[9px] text-slate-400 text-center mt-2">Opens in new tab · Print to save</p>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-5 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                    <i className="fas fa-file-pdf text-slate-300 text-lg"></i>
                  </div>
                  <p className="text-xs font-bold text-slate-600 mb-1">No report yet</p>
                  <p className="text-[10px] text-slate-400 mb-4">Generate your strategy first</p>
                  <Link to="/marketing"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                    <i className="fas fa-chess text-[10px]"></i> Build Strategy
                  </Link>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
